import { useEffect, useMemo, useRef, useState } from 'react';
import { useTemplates, type Template } from '../../../api/templates.api';
import { useUpdateCampaign, type CampaignDetail } from '../../../api/campaigns.api';
import { TemplateInputsForm } from './TemplateInputsForm';
import { JobPicker } from './JobPicker';
import { collectTemplateTokens, templateUsesJobBinding, type StepLike } from './tokens';

/* Wraps TemplateInputsForm with the data-fetching + persistence concerns
   so the form itself stays pure and testable. Sprint 16b.

   - Fetches every template visible to the org once (cached by TanStack).
   - Reads the campaign's sequence.steps to find which templates are
     referenced and unions their merge_fields.
   - Local state is the source of truth while typing; debounced PATCH
     persists into campaign.merge_context. */

const DEBOUNCE_MS = 600;

export interface TemplateInputsCardProps {
  campaign: CampaignDetail;
}

export function TemplateInputsCard({ campaign }: TemplateInputsCardProps) {
  const { data: tmplData } = useTemplates({ include_system: true });
  const update = useUpdateCampaign(campaign.id);

  const templatesById = useMemo(() => {
    const map = new Map<string, Template>();
    for (const t of tmplData?.data ?? []) map.set(t.id, t);
    return map;
  }, [tmplData]);

  const steps = useMemo<StepLike[]>(() => {
    const raw = (campaign.sequence?.steps ?? []) as Array<{ template_id?: string | null }>;
    return raw.map((s) => ({ template_id: s?.template_id ?? null }));
  }, [campaign.sequence?.steps]);

  const tokens = useMemo(
    () => collectTemplateTokens(steps, templatesById),
    [steps, templatesById],
  );

  const [draft, setDraft] = useState<Record<string, string>>(
    (campaign.merge_context as Record<string, string> | null) ?? {},
  );

  // Sync local draft when the campaign row changes from underneath us
  // (e.g. the merge_context was edited in another tab and we refetched).
  // Only resets when the server value's stringification differs from the
  // last value we sent — avoids fighting the user mid-keystroke.
  const lastSentRef = useRef<string>(JSON.stringify(draft));
  useEffect(() => {
    const server = JSON.stringify((campaign.merge_context as Record<string, string> | null) ?? {});
    if (server !== lastSentRef.current) {
      setDraft((campaign.merge_context as Record<string, string> | null) ?? {});
      lastSentRef.current = server;
    }
  }, [campaign.merge_context]);

  // Debounced PATCH on draft changes.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPayload = useRef<Record<string, string> | null>(null);

  const schedulePersist = (next: Record<string, string>) => {
    pendingPayload.current = next;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const payload = pendingPayload.current;
      if (!payload) return;
      const serialized = JSON.stringify(payload);
      // No-op if the value hasn't actually changed since the last send.
      if (serialized === lastSentRef.current) return;
      lastSentRef.current = serialized;
      update.mutate({ merge_context: payload });
    }, DEBOUNCE_MS);
  };

  // Flush any pending PATCH on unmount so a navigate-away doesn't drop
  // the last few keystrokes.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    const payload = pendingPayload.current;
    if (!payload) return;
    if (JSON.stringify(payload) === lastSentRef.current) return;
    lastSentRef.current = JSON.stringify(payload);
    update.mutate({ merge_context: payload });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (next: Record<string, string>) => {
    setDraft(next);
    schedulePersist(next);
  };

  // Sprint 16c — surface the JobPicker when any chosen template references
  // {{job.*}} tokens. Selection PATCHes Campaign.bound_job_id immediately
  // (no debounce — binding is a discrete action).
  const showJobPicker = useMemo(() => templateUsesJobBinding(tokens), [tokens]);
  const handleBindJob = (nextJobId: string | null) => {
    update.mutate({ bound_job_id: nextJobId });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {showJobPicker && (
        <section
          data-testid="job-binding-section"
          style={{
            background: 'var(--card-bg, #fff)',
            border: '1px solid var(--card-border, #ececf2)',
            borderRadius: 'var(--card-radius, 8px)',
            padding: 20,
          }}
        >
          <header style={{ marginBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-brand, #1e1b4b)' }}>
              Bind a job
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-muted, #6b6b7d)' }}>
              The chosen templates use <code style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 12 }}>{'{{job.*}}'}</code>{' '}
              tokens. Pick a job from your JobTetris-synced list to auto-fill them, or leave unbound and fill in the fields manually below.
            </p>
          </header>
          <JobPicker value={campaign.bound_job_id ?? null} onChange={handleBindJob} />
        </section>
      )}
      <TemplateInputsForm
        tokens={tokens}
        value={draft}
        onChange={handleChange}
        bindings={{ bound_job_id: campaign.bound_job_id ?? null }}
        intro="Fields the chosen templates need that aren't auto-resolved from the recipient, your profile, the brand kit, or your default booking link. Saved automatically."
      />
    </div>
  );
}
